function sameUpload(left, right) {
  if (!left || !right) return false;
  if (left.id != null && right.id != null) {
    return String(left.id) === String(right.id);
  }
  return Boolean(left.url && right.url && left.url === right.url);
}

function formatArtworkQuote(text) {
  const display = text.toLowerCase();
  let end = display.length - 1;
  while (end >= 0 && /\s/.test(display[end])) end -= 1;
  // Avoid String.replace: the legacy Babel preset injects ESM into this CommonJS module.
  // Drop a single terminal period, retaining whitespace and consecutive dots/ellipses.
  if (display[end] === '.' && display[end - 1] !== '.') {
    return display.slice(0, end) + display.slice(end + 1);
  }
  return display;
}

function approvedQuoteSignature(quotes) {
  const safeQuotes = Array.isArray(quotes) ? quotes : [];
  return JSON.stringify(safeQuotes.map(quote => [String(quote.id), quote.quote]));
}

const INTRO_MS = 3000;
const WHITE_HOLD_MS = 3000;

function normalizeQuoteCount(value) {
  const count = Math.floor(value - 0);
  return Math.abs(count) !== Infinity && count > 0 ? count : 0;
}

function quoteTimelineRange(quoteCount) {
  return {
    min: 0,
    max: normalizeQuoteCount(quoteCount),
  };
}

function clampQuotePosition(value, quoteCount) {
  const total = normalizeQuoteCount(quoteCount);
  const position = Math.round(value - 0);
  const safePosition = Math.abs(position) === Infinity ? 0 : (position || 0);
  return Math.max(0, Math.min(total, safePosition));
}

function buildQuoteSeekState(quoteCount, requestedPosition) {
  const total = normalizeQuoteCount(quoteCount);
  const position = clampQuotePosition(requestedPosition, total);
  const phase = position === 0 ? 'intro' : 'white-hold';
  const remainingMs = position === 0 ? INTRO_MS : WHITE_HOLD_MS;
  return {
    completed: false,
    phase: total === 0 ? 'empty' : phase,
    position,
    remainingMs: total === 0 ? 0 : remainingMs,
    settledCount: Math.max(0, position - 1),
    total,
  };
}

function advanceQuoteTimeline(state) {
  const current = state || buildQuoteSeekState(0, 0);
  if (current.phase === 'empty') {
    return buildQuoteSeekState(current.total, 0);
  }
  if (current.phase === 'intro') {
    return current.total > 0 ? buildQuoteSeekState(current.total, 1) : current;
  }
  if (current.phase === 'white-hold') {
    if (current.position >= current.total) {
      return {
        completed: true,
        phase: 'completed',
        position: current.total,
        remainingMs: 0,
        settledCount: current.total,
        total: current.total,
      };
    }
    return {
      completed: false,
      phase: 'white-hold',
      position: current.position + 1,
      remainingMs: WHITE_HOLD_MS,
      settledCount: current.position,
      total: current.total,
    };
  }
  return current;
}

function remainingQuotePhaseMs(dueAt, now) {
  const remaining = (dueAt - 0) - (now - 0);
  const safeRemaining = Math.abs(remaining) === Infinity ? 0 : (remaining || 0);
  return Math.max(0, safeRemaining);
}

function restartQuoteTimeline(quoteCount) {
  return buildQuoteSeekState(quoteCount, 0);
}

function placementsAtQuotePosition(placementPlan, quoteCount, position) {
  const safePlan = Array.isArray(placementPlan) ? placementPlan : [];
  return safePlan.slice(0, clampQuotePosition(position, quoteCount));
}

function buildPlaybackSteps(serverSteps, project) {
  const safeSteps = Array.isArray(serverSteps) ? serverSteps.slice() : [];
  const artwork = project && project.quoteBackgroundArtwork;

  if (!project || !project.interactiveQuotesEnabled || !artwork || !artwork.url) {
    return safeSteps;
  }

  let matchIndex = -1;
  for (let index = 0; index < safeSteps.length; index += 1) {
    const step = safeSteps[index];
    if (sameUpload(step && step.image, artwork)) {
      matchIndex = index;
      break;
    }
  }

  if (matchIndex !== -1) {
    safeSteps[matchIndex] = Object.assign({}, safeSteps[matchIndex], {
      quoteArtwork: artwork,
      isQuoteArtwork: true,
    });
    return safeSteps;
  }

  safeSteps.push({
    id: `quote-artwork-${project.id}`,
    order: null,
    text: '',
    image: null,
    audio: null,
    video: null,
    quoteArtwork: artwork,
    isQuoteArtwork: true,
  });
  return safeSteps;
}

function normalizeApprovedQuotes(response) {
  if (!response || !Array.isArray(response.quotes)) return [];
  const seenIds = Object.create(null);
  return response.quotes.reduce((quotes, record) => {
    if (!record || record.id == null || typeof record.quote !== 'string') return quotes;
    const normalizedId = String(record.id);
    if (seenIds[normalizedId]) return quotes;
    seenIds[normalizedId] = true;
    quotes.push({ id: record.id, quote: record.quote });
    return quotes;
  }, []);
}

module.exports = {
  INTRO_MS,
  WHITE_HOLD_MS,
  advanceQuoteTimeline,
  approvedQuoteSignature,
  buildQuoteSeekState,
  buildPlaybackSteps,
  formatArtworkQuote,
  normalizeApprovedQuotes,
  placementsAtQuotePosition,
  quoteTimelineRange,
  remainingQuotePhaseMs,
  restartQuoteTimeline,
  sameUpload,
};
